/**
 * File: generation.ts
 * Description: TypeScript type definitions for Architecture Generation System
 * Author: System
 * Created: 2025-08-09
 * Last Modified: 2025-08-01
 */

// ====================
// API Schema Types (matching backend)
// ====================

export enum ArchitectureSection {
  BUSINESS_PURPOSE = 'business_purpose',
  SOLUTION_SUMMARY = 'solution_summary', 
  ADR_SUMMARY = 'adr_summary',
  CONCEPTUAL_ARCHITECTURE = 'conceptual_architecture',
  DATA_ARCHITECTURE = 'data_architecture',
  INTEGRATION_ARCHITECTURE = 'integration_architecture',
  IMPACTED_COMPONENTS = 'impacted_components',
  SECURITY_ARCHITECTURE = 'security_architecture',
  TECHNOLOGY_ARCHITECTURE = 'technology_architecture',
  DEPLOYMENT_MODEL = 'deployment_model',
  RISK_ASSESSMENT = 'risk_assessment',
  THREAT_MODELING = 'threat_modeling'
}

/** Number of priority-1 architecture sections used in generation. */
export const TOTAL_ARCHITECTURE_SECTIONS = 9;

export type GenerationDataValue =
  | string
  | number
  | boolean
  | null
  | GenerationDataMap
  | GenerationDataValue[];

export interface GenerationDataMap {
  [key: string]: GenerationDataValue;
}

export interface GenerateRequest {
  input: string;
  section: ArchitectureSection;
}

export interface GenerationResponse {
  project_id: number;
  section: ArchitectureSection;
  success: boolean;
}

export interface SectionContentRequest {
  content: string;
}

export interface SectionResponse {
  project_id: number;
  section: ArchitectureSection | null;
  section_content: string | null;
  section_groundedness: number | null;
  section_updated_at?: string | null;
  section_ungrounded_items?: Record<string, string> | null;
}

// ====================
// Core Generation Types
// ====================

export enum GenerationMode {
  COMPLETE = 'complete',           // Generate all sections in sequence
  SELECTIVE = 'selective',         // Generate selected sections only
  SINGLE = 'single'               // Generate one section at a time
}

export enum GenerationStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  ERROR = 'error',
  CANCELLED = 'cancelled'
}

export enum SectionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ERROR = 'error',
  SKIPPED = 'skipped',
  APPROVED = 'approved',
  NEEDS_REVISION = 'needs_revision'
}

// ====================
// Progress Tracking
// ====================

export interface GenerationProgress {
  totalSections: number;
  completedSections: number;
  currentSectionIndex: number;
  overallPercentage: number;
  estimatedTimeRemaining: number; // in milliseconds
  startTime: Date;
  estimatedEndTime?: Date;
}

export interface SectionProgress {
  sectionId: string;
  sectionName: string;
  status: SectionStatus;
  percentage: number;
  startTime?: Date;
  endTime?: Date;
  estimatedDuration: number;
  actualDuration?: number;
  retryCount: number;
  errorMessage?: string;
}

// ====================
// Generated Content
// ====================

export interface GeneratedSection {
  id: string;
  section: ArchitectureSection;
  title: string;
  content: string;
  status: SectionStatus;
  groundednessScore: number;
  references: KnowledgeReference[];
  createdAt: Date;
  updatedAt: Date;
  metadata?: {
    ungroundedItems?: Record<string, string> | null;
  };
}

export interface SectionMetadata {
  wordCount: number;
  tokenCount: number;
  modelUsed: string;
  generationDuration: number;
  promptTokens: number;
  completionTokens: number;
  estimatedCost: number;
  temperature: number;
  retryCount: number;
}

export interface GeneratedDiagram {
  id: string;
  type: DiagramType;
  title: string;
  description: string;
  format: DiagramFormat;
  content: string; // Mermaid code, SVG, or other format
  metadata: DiagramMetadata;
  references: KnowledgeReference[];
}

export enum DiagramType {
  SYSTEM_CONTEXT = 'system_context',
  CONTAINER = 'container',
  COMPONENT = 'component',
  DATA_FLOW = 'data_flow',
  SEQUENCE = 'sequence',
  DEPLOYMENT = 'deployment',
  NETWORK = 'network',
  SECURITY = 'security'
}

export enum DiagramFormat {
  MERMAID = 'mermaid',
  SVG = 'svg',
  DRAWIO = 'drawio',
  PLANTUML = 'plantuml'
}

export interface DiagramMetadata {
  complexity: 'low' | 'medium' | 'high';
  componentCount: number;
  connectionCount: number;
  generatedAt: Date;
  lastModified: Date;
}

// ====================
// Quality & Grounding
// ====================

export interface QualityMetrics {
  groundednessScore: number;        // 0-100: How well supported by references
}

export enum ConfidenceLevel {
  HIGH = 'high',         // 80-100%
  MEDIUM = 'medium',     // 60-79%
  LOW = 'low'           // 0-59%
}

export interface QualityIssue {
  id: string;
  type: IssueType;
  severity: IssueSeverity;
  description: string;
  location?: ContentLocation;
  suggestedFix?: string;
  references?: string[];
}

export enum IssueType {
  MISSING_REFERENCE = 'missing_reference',
  INCONSISTENCY = 'inconsistency',
  COMPLIANCE_VIOLATION = 'compliance_violation',
  INCOMPLETE_CONTENT = 'incomplete_content',
  CLARITY_ISSUE = 'clarity_issue',
  OUTDATED_REFERENCE = 'outdated_reference'
}

export enum IssueSeverity {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  INFO = 'info'
}

export interface QualitySuggestion {
  id: string;
  type: SuggestionType;
  description: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'high' | 'medium' | 'low';
  autoApplicable: boolean;
}

export enum SuggestionType {
  ADD_REFERENCE = 'add_reference',
  EXPAND_CONTENT = 'expand_content',
  IMPROVE_CLARITY = 'improve_clarity',
  FIX_INCONSISTENCY = 'fix_inconsistency',
  UPDATE_COMPLIANCE = 'update_compliance'
}

export interface ContentLocation {
  sectionId: string;
  paragraphIndex?: number;
  sentenceIndex?: number;
  characterStart?: number;
  characterEnd?: number;
}

// ====================
// Knowledge References
// ====================

export interface KnowledgeReference {
  id: string;
  sourceId: string;
  sourceName: string;
  sourceType: ReferenceSourceType;
  title: string;
  excerpt: string;
  url?: string;
  confidence: number;         // 0-100: How relevant this reference is
  usageContext: string;       // Where/how this reference was used
  retrievalScore: number;     // 0-1: Similarity score from vector search
  timestamp: Date;
  metadata: ReferenceMetadata;
}

export enum ReferenceSourceType {
  KNOWLEDGE_BASE = 'knowledge_base',
  UPLOADED_DOCUMENT = 'uploaded_document',
  SHELL_STANDARD = 'shell_standard',
  HISTORICAL_ARCHITECTURE = 'historical_architecture',
  ADR = 'adr',
  EXTERNAL_STANDARD = 'external_standard',
  BEST_PRACTICE = 'best_practice'
}

export interface ReferenceMetadata {
  documentType: string;
  authoredBy?: string;
  lastUpdated?: Date;
  version?: string;
  confidenceRating: string;
  approved: boolean;
  tags: string[];
}

// ====================
// Activity Logging
// ====================

export interface GenerationLogEntry {
  id: string;
  timestamp: Date;
  type: 'thinking' | 'action' | 'reference' | 'result' | 'error';
  category: 'generation' | 'analysis' | 'research' | 'validation';
  message: string;
  details?: LogDetails;
  sectionId?: string;
  agentName?: string;
  thinkingProcess?: string;
  confidence?: number;
  references?: KnowledgeReference[];
  duration?: number;
  metadata?: GenerationDataMap;
}

export enum LogType {
  THINKING = 'thinking',     // LLM internal reasoning
  ACTION = 'action',         // Concrete action taken
  REFERENCE = 'reference',   // Knowledge base lookup
  RESULT = 'result',         // Generation output
  ERROR = 'error'           // Error or issue
}

export enum LogCategory {
  GENERATION = 'generation',  // Section generation
  ANALYSIS = 'analysis',      // Context analysis
  RESEARCH = 'research',      // Knowledge lookup
  VALIDATION = 'validation'   // Quality checking
}

export interface LogDetails {
  action?: string;
  target?: string;
  previousValue?: GenerationDataValue;
  newValue?: GenerationDataValue;
  reason?: string;
  context?: GenerationDataMap;
  references?: string[];
  metrics?: Record<string, number>;
  thinkingSteps?: string[];
}

// ====================
// Context & Project Summary
// ====================

export interface ProjectContext {
  projectId: string;
  projectTitle: string;
  description?: string;
  uploadedDocuments: UploadedDocument[];
  adrSummary: ADRSummary;
  adrCount?: number;
  knowledgeBaseSources: KnowledgeSource[];
  requirements: ProjectRequirement[];
  constraints: ProjectConstraint[];
  metadata: ProjectMetadata;
  instructionsUpdatedAt?: string;
  adrUpdatedAt?: string;
}

export interface UploadedDocument {
  id: string;
  filename: string;
  type: DocumentType;
  size: number;
  uploadedAt: Date;
  processedAt?: Date;
  status: DocumentStatus;
  extractedContent?: string;
  summaryContent?: string;
  keyInsights?: string[];
  metadata: DocumentMetadata;
  updated_at?: string;
}

export enum DocumentType {
  BRD = 'brd',                    // Business Requirements Document
  TECHNICAL_SPEC = 'technical_spec',
  EXISTING_ARCHITECTURE = 'existing_architecture',
  COMPLIANCE_DOCUMENT = 'compliance_document',
  SECURITY_GUIDELINES = 'security_guidelines',
  INTEGRATION_SPEC = 'integration_spec',
  USER_STORY = 'user_story',
  OTHER = 'other'
}

export enum DocumentStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  ERROR = 'error',
  DELETED = 'deleted'
}

export interface DocumentMetadata {
  contentType: string;
  encoding?: string;
  pages?: number;
  language?: string;
  author?: string;
  createdDate?: Date;
  lastModified?: Date;
  extractionMethod: string;
  processingDuration: number;
}

export interface ADRSummary {
  totalDefined: number;
  totalGenerated: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
  completionPercentage: number;
  lastUpdated: Date;
  pendingDefinitions: string[];
  generatedAdrs: string[];
}

export interface KnowledgeSource {
  id: string;
  name: string;
  type: KnowledgeSourceType;
  description: string;
  isAvailable: boolean;
  lastSyncTime: Date;
  documentCount: number;
  coverage: SourceCoverage;
  trustLevel: TrustLevel;
}

export enum KnowledgeSourceType {
  ENTERPRISE_WIKI = 'enterprise_wiki',
  ARCHITECTURE_REPOSITORY = 'architecture_repository',
  STANDARDS_LIBRARY = 'standards_library',
  BEST_PRACTICES = 'best_practices',
  SECURITY_GUIDELINES = 'security_guidelines',
  COMPLIANCE_DOCS = 'compliance_docs',
  VENDOR_DOCS = 'vendor_docs'
}

export interface SourceCoverage {
  architecturePatterns: number;    // 0-100%
  securityGuidelines: number;      // 0-100%
  integrationPatterns: number;     // 0-100%
  technologyStandards: number;     // 0-100%
  complianceRequirements: number;  // 0-100%
}

export enum TrustLevel {
  VERIFIED = 'verified',           // Official Shell approved content
  TRUSTED = 'trusted',             // Peer reviewed and validated
  COMMUNITY = 'community',         // Community contributed
  EXTERNAL = 'external'            // External sources
}

export interface ProjectRequirement {
  id: string;
  type: RequirementType;
  priority: RequirementPriority;
  description: string;
  source: string;
  status: RequirementStatus;
  tags: string[];
}

export enum RequirementType {
  FUNCTIONAL = 'functional',
  NON_FUNCTIONAL = 'non_functional',
  SECURITY = 'security',
  COMPLIANCE = 'compliance',
  INTEGRATION = 'integration',
  PERFORMANCE = 'performance',
  SCALABILITY = 'scalability'
}

export enum RequirementPriority {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low'
}

export enum RequirementStatus {
  IDENTIFIED = 'identified',
  ANALYZED = 'analyzed',
  ADDRESSED = 'addressed',
  VALIDATED = 'validated'
}

export interface ProjectConstraint {
  id: string;
  type: ConstraintType;
  description: string;
  impact: 'high' | 'medium' | 'low';
  source: string;
  mitigationStrategy?: string;
}

export enum ConstraintType {
  TECHNICAL = 'technical',
  BUSINESS = 'business',
  REGULATORY = 'regulatory',
  RESOURCE = 'resource',
  TIME = 'time',
  BUDGET = 'budget',
  SECURITY = 'security'
}

export interface ProjectMetadata {
  createdAt: Date;
  lastModified: Date;
  createdBy: string;
  organizationalUnit: string;
  businessDomain: string;
  technologyDomain: string;
  estimatedComplexity: ProjectComplexity;
  estimatedDuration: number; // in days
  budgetRange?: BudgetRange;
  confidentialityLevel: ConfidentialityLevel;
}

export enum ProjectComplexity {
  LOW = 'low',           // Single domain, standard patterns
  MEDIUM = 'medium',     // Multiple domains, some custom patterns
  HIGH = 'high',         // Complex integrations, novel patterns
  VERY_HIGH = 'very_high' // Enterprise-wide, cutting-edge technology
}

export enum BudgetRange {
  UNDER_100K = 'under_100k',
  RANGE_100K_500K = '100k_500k',
  RANGE_500K_1M = '500k_1m',
  RANGE_1M_5M = '1m_5m',
  OVER_5M = 'over_5m'
}

export enum ConfidentialityLevel {
  PUBLIC = 'public',
  INTERNAL = 'internal',
  CONFIDENTIAL = 'confidential',
  RESTRICTED = 'restricted'
}

// ====================
// Generation Configuration
// ====================

export interface GenerationConfig {
  mode: GenerationMode;
  selectedSections: ArchitectureSection[];
  options: GenerationOptions;
  templates: TemplateConfig;
  qualitySettings: QualitySettings;
  outputSettings: OutputSettings;
}

export interface GenerationOptions {
  includeReferences: boolean;
  includeDiagrams: boolean;
  diagramFormats: DiagramFormat[];
  detailLevel: DetailLevel;
  tone: GenerationTone;
  audienceLevel: AudienceLevel;
  customInstructions?: string;
  modelSettings: ModelSettings;
}

export enum DetailLevel {
  HIGH_LEVEL = 'high_level',     // Executive summary level
  DETAILED = 'detailed',         // Standard architectural detail
  COMPREHENSIVE = 'comprehensive' // Deep technical detail
}

export enum GenerationTone {
  FORMAL = 'formal',
  PROFESSIONAL = 'professional',
  TECHNICAL = 'technical',
  CONVERSATIONAL = 'conversational'
}

export enum AudienceLevel {
  EXECUTIVE = 'executive',       // C-level, minimal technical detail
  MANAGEMENT = 'management',     // IT managers, some technical detail
  TECHNICAL = 'technical',       // Architects and developers
  SPECIALIST = 'specialist'      // Domain experts, maximum detail
}

export interface ModelSettings {
  primaryModel: string;
  fallbackModel: string;
  temperature: number;
  maxTokens: number;
  topP: number;
  frequencyPenalty: number;
  presencePenalty: number;
}

export interface TemplateConfig {
  architectureTemplate: string;
  sectionTemplates: Record<ArchitectureSection, string>;
  customTemplates: Record<string, string>;
}

export interface QualitySettings {
  minimumGroundednessScore: number;
  requireReferences: boolean;
  enableRealTimeChecking: boolean;
  autoRetryOnLowQuality: boolean;
  maxRetryAttempts: number;
}

export interface OutputSettings {
  format: OutputFormat[];
  includeMetadata?: boolean;
  includeQualityReport: boolean;
  includeReferences: boolean;
  customStyles?: Record<string, string>;
}

export enum OutputFormat {
  MARKDOWN = 'markdown',
  PDF = 'pdf',
  WORD = 'word',
  HTML = 'html',
  CONFLUENCE = 'confluence',
  WIKI = 'wiki'
}

// ====================
// State Management
// ====================

export interface GenerationState {
  // Control state
  status: GenerationStatus;
  config: GenerationConfig;
  isInitialized: boolean;
  canPause: boolean;
  canResume: boolean;
  canCancel: boolean;
  
  // Progress tracking
  progress: GenerationProgress;
  sectionProgress: Record<string, SectionProgress>;
  currentSection: ArchitectureSection | null;
  
  // Generated content
  sections: Record<string, GeneratedSection>;
  metadata: GenerationMetadata;
  
  // Quality and references
  overallQuality: QualityMetrics;
  references: KnowledgeReference[];
  
  // Activity logging
  logs: GenerationLogEntry[];
  errors: GenerationError[];
  
  // UI state
  activeTab: string;
  selectedSection: string | null;
  showActivityLog: boolean;
  showQualityPanel: boolean;
  
  // Session management
  sessionId: string;
  lastSaved: Date;
  autoSaveEnabled: boolean;
}

export interface GenerationMetadata {
  sessionId: string;
  startTime: Date;
  endTime?: Date;
  totalDuration?: number;
  totalTokensUsed?: number;
  totalCost?: number;
  modelsUsed?: string[];
  version: string;
  generatedBy: string;
  retryCount?: number;
  pauseCount?: number;
}

export interface GenerationError {
  id: string;
  timestamp: Date;
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  details?: string;
  section?: ArchitectureSection;
  stackTrace?: string;
  recoverable: boolean;
  retryCount: number;
  resolved: boolean;
  resolution?: string;
}

export enum ErrorType {
  NETWORK_ERROR = 'network_error',
  API_ERROR = 'api_error',
  TIMEOUT_ERROR = 'timeout_error',
  QUOTA_EXCEEDED = 'quota_exceeded',
  CONTENT_FILTER = 'content_filter',
  VALIDATION_ERROR = 'validation_error',
  SYSTEM_ERROR = 'system_error',
  USER_ERROR = 'user_error'
}

export enum ErrorSeverity {
  FATAL = 'fatal',       // Stops entire generation
  BLOCKING = 'blocking', // Stops current section
  WARNING = 'warning',   // Continues with degraded quality
  INFO = 'info'         // Informational only
}

// ====================
// API Interfaces
// ====================

export interface GenerationRequestAPI {
  projectId: string;
  config: GenerationConfig;
  context: ProjectContext;
  sessionId?: string;
  resumeFromState?: GenerationState;
}

export interface GenerationResponseAPI {
  success: boolean;
  sessionId: string;
  message?: string;
  error?: GenerationError;
  state?: GenerationState;
}

export interface PauseRequest {
  sessionId: string;
  reason?: string;
}

export interface ResumeRequest {
  sessionId: string;
  modifiedConfig?: Partial<GenerationConfig>;
}

export interface QualityCheckRequest {
  sectionId: string;
  content: string;
  references: KnowledgeReference[];
  context: ProjectContext;
}

export interface QualityCheckResponse {
  success: boolean;
  metrics: QualityMetrics;
  suggestions: QualitySuggestion[];
  issues: QualityIssue[];
}

// ====================
// UI Component Props
// ====================

export interface GenerationDashboardProps {
  projectContext: ProjectContext;
  generationState: GenerationState;
  logs: GenerationLogEntry[];
  sectionsGenerating?: Set<ArchitectureSection>;
  pendingGroundedness?: Set<ArchitectureSection>;
  selectedSection?: string | null;
  onConfigChange: (config: GenerationConfig) => void;
  onStartGeneration: () => void;
  onPauseGeneration: () => void;
  onResumeGeneration: () => void;
  onCancelGeneration: () => void;
  onGenerateSection?: (section: ArchitectureSection, isRegeneration?: boolean) => Promise<void>;
  onUpdateSection?: (section: ArchitectureSection, content: string) => Promise<void>;
  onSectionSelect?: (sectionId: string | null) => void;
}

export interface ActivityLogProps {
  logs: GenerationLogEntry[];
  isLive: boolean;
  maxEntries?: number;
  filters?: LogType[];
  onClearLogs: () => void;
  onExportLogs: () => void;
}

export interface QualityPanelProps {
  section: GeneratedSection;
  onApprove: (sectionId: string) => void;
  onRequestRevision: (sectionId: string, feedback: string) => void;
  onViewReferences: (references: KnowledgeReference[]) => void;
}

// ====================
// Event Types
// ====================

export interface GenerationEvent {
  type: GenerationEventType;
  timestamp: Date;
  sessionId: string;
  data?: GenerationDataValue;
}

export enum GenerationEventType {
  GENERATION_STARTED = 'generation_started',
  GENERATION_PAUSED = 'generation_paused',
  GENERATION_RESUMED = 'generation_resumed',
  GENERATION_COMPLETED = 'generation_completed',
  GENERATION_CANCELLED = 'generation_cancelled',
  GENERATION_ERROR = 'generation_error',
  SECTION_STARTED = 'section_started',
  SECTION_COMPLETED = 'section_completed',
  QUALITY_CHECK_COMPLETED = 'quality_check_completed',
  REFERENCE_RETRIEVED = 'reference_retrieved',
  USER_INTERACTION = 'user_interaction'
}

// ====================
// Utility Types
// ====================

export type GenerationCallback = (event: GenerationEvent) => void;
export type QualityCallback = (metrics: QualityMetrics) => void;
export type ProgressCallback = (progress: GenerationProgress) => void;
export type LogCallback = (entry: GenerationLogEntry) => void;

export interface Comment {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  timestamp: Date;
  location?: ContentLocation;
  resolved: boolean;
  replies?: Comment[];
}

// ====================
// Export All Types
// ====================

// All interfaces and types are already exported above with their declarations
// No need for additional export declarations
